import React, { useState, useRef, useEffect } from "react";
import IconArrowDownSrc from "assets/arrow-down.svg";
import IconXSrc from "assets/x-black.svg";

const SelectInfiniteCustom = ({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  isLoading = false,
  hasMore = false,
  onLoadMore,
  isClearable = false,
  isDisabled = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const observerTarget = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !observerTarget.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && onLoadMore) {
          onLoadMore();
        }
      },
      { threshold: 1.0 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [isOpen, hasMore, isLoading, onLoadMore]);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`flex items-center justify-between px-3 py-2 border rounded cursor-pointer min-h-[40px] ${
          isDisabled
            ? "bg-gray-100 cursor-not-allowed"
            : "bg-white hover:border-[#D1D5DB]"
        } ${isOpen ? "border-[#F3F4F6] ring-1 ring-[#F3F4F6]" : "border-[#E5E7EB]"}`}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <span
          className={`text-[14px] ${
            value ? "text-[#252526]" : "text-[#9CA3AF]"
          }`}
        >
          {value?.label || placeholder}
        </span>
        <div className="flex items-center gap-2">
          {isClearable && value && !isDisabled && (
            <img
              src={IconXSrc}
              alt="Clear"
              className="w-3 h-3 cursor-pointer hover:opacity-70"
              onClick={handleClear}
            />
          )}
          <img
            src={IconArrowDownSrc}
            alt="Toggle"
            className={`w-4 h-4 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-[99999] w-full mt-1 bg-white border border-[#E5E7EB] rounded shadow-lg max-h-[300px] overflow-hidden">
          <div className="p-2 border-b border-[#E5E7EB]">
            <input
              type="text"
              className="w-full px-3 py-2 text-[14px] border border-[#E5E7EB] rounded focus:outline-none focus:border-[#F3F4F6] focus:ring-1 focus:ring-[#F3F4F6]"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="overflow-y-auto max-h-[250px]">
            {filteredOptions.length === 0 && !isLoading ? (
              <div className="px-3 py-2 text-[14px] text-[#9CA3AF] text-center">
                No options
              </div>
            ) : (
              <>
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-3 py-2 text-[14px] cursor-pointer hover:bg-[#F3F4F6] ${
                      value?.value === option.value
                        ? "bg-[#F3F4F6] text-[#252526] hover:bg-[#F3F4F6]"
                        : "text-[#252526]"
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </div>
                ))}

                {isLoading && (
                  <div className="px-3 py-2 text-[14px] text-[#9CA3AF] text-center">
                    Loading...
                  </div>
                )}

                {!isLoading && hasMore && (
                  <div className="px-3 py-1 text-[12px] text-[#9CA3AF] text-center">
                    Scroll for more
                  </div>
                )}

                <div ref={observerTarget} style={{ height: "1px" }} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectInfiniteCustom;
